import * as API from '@/API';
import { endSession, nextRound as getNextRound, setSharing, setEnding } from '@/client/session';
import { HostSessionContext } from '@/contexts/HostSessionContext';
import { reactionForArtworkReaction } from '@/components/Controls/ReactionsBar';
import { gql, useQuery, useSubscription } from '@apollo/client';
import { GraphQLQuery, GraphQLSubscription } from 'aws-amplify/api';
import { ReactNode, useState } from 'react';

export const sessionQuery = gql`
  query Session($sessionID: ID!) {
    sessionGet(id: $sessionID) {
      id
      data {
        status
        players {
          id
          name
          gameStatus
          connectionStatus
          feelingBefore
          feelingAfter
          question
          questionAnswer
        }
        rounds {
          id
          index
          isCurrent
          isLast
          promptId
          artwork {
            id
            drawing
            playerId
          }
        }
        questionType
      }
    }
  }
`;

export const artworkReactionsQuery = gql`
  query HostArtworkReactions($sessionID: ID!) {
    artworkReactionList(filter: { sessionId: $sessionID }) {
      reaction
      playerCardId
      playerId
      sessionId
    }
  }
`;

const hostSubscription = gql`
  subscription HostSessionUpdated($id: ID!) {
    hostSessionUpdated(id: $id) {
      __typename
      ... on NewPlayerUpdate {
        player {
          id
          name
          gameStatus
          connectionStatus
          feelingBefore
          feelingAfter
          question
          questionAnswer
        }
      }
      ... on NewDrawingUpdate {
        artwork {
          drawing
          id
          playerId
        }
      }
      ... on NewReactionUpdate {
        reaction {
          reaction
          playerCardId
          playerId
          sessionId
        }
      }
      ... on SessionStatusUpdate {
        status
      }
    }
  }
`;

/**
 * A state management function that provides session controls to the app
 * through the SessionContext that is accessible through the useSessionContext hook.
 */
function SessionControls({ sessionID }: { sessionID: string }) {
  // State
  const [state, setState] = useState<'initializing' | 'loading' | 'connected' | 'disconnected'>(
    'initializing'
  );
  const [session, setSession] = useState<API.SessionData>();
  const [artworkReactions, setArtworkReactions] = useState<API.RedisArtworkReaction[]>([]);

  const currentRound = session?.rounds?.find((round) => round?.isCurrent);
  const currentArtwork = currentRound?.artwork;

  useQuery<GraphQLQuery<API.SessionGetQuery>>(sessionQuery, {
    variables: { sessionID },
    onCompleted: ({ sessionGet }) => {
      if (sessionGet) {
        setSession(sessionGet.data as API.SessionData);
        setState('connected');
      }
    },
    onError: () => {
      setState('disconnected');
    }
  });

  // Query for artwork reactions
  useQuery<GraphQLQuery<API.ArtworkReactionListQuery>>(artworkReactionsQuery, {
    variables: { sessionID },
    onCompleted: ({ artworkReactionList }) => {
      if (artworkReactionList) {
        setArtworkReactions(artworkReactionList as API.RedisArtworkReaction[]);
      }
    },
    onError: (error) => {
      console.error('Error fetching artwork reactions:', error);
    }
  });

  // Use host subscription for general session updates
  useSubscription<GraphQLSubscription<API.HostSessionUpdatedSubscription>>(hostSubscription, {
    variables: { id: sessionID },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      if (session && data?.hostSessionUpdated?.__typename === 'NewPlayerUpdate') {
        setSession({
          ...session,
          players: [...session.players, data.hostSessionUpdated.player]
        } as API.SessionData);
      }
      if (session && data?.hostSessionUpdated?.__typename === 'NewDrawingUpdate') {
        if (!data.hostSessionUpdated?.artwork) {
          return;
        }

        const artwork = {
          ...data.hostSessionUpdated.artwork,
          __typename: 'Artwork'
        } as API.Artwork;

        setSession({
          ...session,
          rounds: session.rounds.map((sessionRound) => {
            if (sessionRound.isCurrent) {
              return {
                ...sessionRound,
                artwork: [...sessionRound.artwork, artwork]
              };
            }

            return sessionRound;
          }),
          players: session.players.map((player) => {
            if (player.id === artwork.playerId) {
              return {
                ...player,
                gameStatus: API.PlayerGameStatus.SUBMITTED
              };
            }
            return player;
          })
        });
      }
      if (session && data?.hostSessionUpdated?.__typename === 'NewReactionUpdate') {
        if (!data.hostSessionUpdated?.reaction) {
          return;
        }

        setArtworkReactions([
          ...artworkReactions,
          data.hostSessionUpdated.reaction as API.RedisArtworkReaction
        ]);
      }
      if (session && data?.hostSessionUpdated?.__typename === 'SessionStatusUpdate') {
        setSession({
          ...session,
          status: data.hostSessionUpdated.status
        } as API.SessionData);
      }
    }
  });

  const setSharingPlayer = async (id: string) => {
    if (!id || !session) return;

    const sharingResult = await setSharing({ id: sessionID, playerId: id });

    if (!sharingResult) return;

    setSession({
      ...session,
      status: API.SessionStatus.sharing,
      players: session.players.map((player) => {
        return {
          ...player,
          gameStatus:
            player.id === sharingResult.playerId
              ? API.PlayerGameStatus.SHARING
              : player.gameStatus === API.PlayerGameStatus.SHARING
                ? API.PlayerGameStatus.SHARED
                : player.gameStatus
        };
      })
    } as API.SessionData);
  };

  const nextRoundFunc = async () => {
    if (!session) return;

    const nextRound = await getNextRound({ id: sessionID });

    if (!nextRound) return;

    const { round, sessionStatus, playerStatus } = nextRound;

    setSession({
      ...session,
      status: sessionStatus,
      rounds: session.rounds.map((sessionRound) => {
        if (sessionRound.index === round) {
          return {
            ...sessionRound,
            isCurrent: true
          };
        }

        return {
          ...sessionRound,
          isCurrent: false
        };
      }),
      players: session.players.map((player) => {
        return {
          ...player,
          gameStatus: playerStatus
        };
      })
    } as API.SessionData);
  };

  const endSessionFunc = async () => {
    await endSession({ id: sessionID });
    if (session) {
      setSession({
        ...session,
        status: API.SessionStatus.ended
      } as API.SessionData);
    }
  };

  const setEndingFunc = async () => {
    await setEnding({ id: sessionID });
  };

  return {
    state,
    currentRound,
    artwork: currentArtwork ?? [],
    players: session?.players ?? [],
    session,
    sessionId: sessionID,
    reactions: artworkReactions.map(reactionForArtworkReaction),
    setSharingPlayer: setSharingPlayer,
    startSession: nextRoundFunc,
    nextGame: nextRoundFunc,
    endSession: endSessionFunc,
    setEnding: setEndingFunc
  };
}

/**
 * Session provider component. This will provide the session context to all
 * child components of this component so that they can access the session state
 * after connecting to a session.
 * @param { SessionProps } props The props for the session provider.
 */
export default function HostSessionProvider({
  sessionID,
  children
}: {
  sessionID: string;
  children: ReactNode;
}) {
  const session = SessionControls({ sessionID });

  return <HostSessionContext.Provider value={session}>{children}</HostSessionContext.Provider>;
}
